import React, { useState, useEffect } from "react";
import "./PWAInstallPrompt.css";

const ShareIcon = () => (
  <svg className="pwa-ios-share-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="8 6 12 2 16 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
    <path d="M20 21H4a2 2 0 01-2-2V10a2 2 0 012-2h3" />
    <path d="M17 8h3a2 2 0 012 2v9a2 2 0 01-2 2H4" />
  </svg>
);

function IOSGuide({ onDismiss }) {
  return (
    <div className="pwa-ios-sheet" role="dialog" aria-label="Install Vivah Bandh">
      <div className="pwa-ios-sheet__inner">
        <button className="pwa-ios-sheet__close" onClick={onDismiss} aria-label="Dismiss">✕</button>

        <div className="pwa-ios-sheet__header">
          <div className="pwa-ios-sheet__appicon">💍</div>
          <div>
            <div className="pwa-ios-sheet__title">Install Vivah Bandh</div>
            <div className="pwa-ios-sheet__subtitle">Add to your Home Screen for a faster, app-like experience</div>
          </div>
        </div>

        <div className="pwa-ios-sheet__divider" />

        <div className="pwa-ios-sheet__steps">
          <div className="pwa-ios-sheet__step">
            <div className="pwa-ios-sheet__step-num">1</div>
            <div className="pwa-ios-sheet__step-body">
              <div className="pwa-ios-sheet__step-label">
                Tap the <span className="pwa-ios-sheet__share-badge"><ShareIcon /> Share</span> button
              </div>
              <div className="pwa-ios-sheet__step-hint">Found at the bottom of Safari's toolbar</div>
            </div>
          </div>

          <div className="pwa-ios-sheet__step">
            <div className="pwa-ios-sheet__step-num">2</div>
            <div className="pwa-ios-sheet__step-body">
              <div className="pwa-ios-sheet__step-label">
                Tap <strong>"Add to Home Screen"</strong>
              </div>
              <div className="pwa-ios-sheet__step-hint">Scroll down in the Share menu if you don't see it</div>
            </div>
          </div>

          <div className="pwa-ios-sheet__step">
            <div className="pwa-ios-sheet__step-num">3</div>
            <div className="pwa-ios-sheet__step-body">
              <div className="pwa-ios-sheet__step-label">
                Tap <strong>"Add"</strong> to confirm
              </div>
              <div className="pwa-ios-sheet__step-hint">The app icon will appear on your Home Screen</div>
            </div>
          </div>
        </div>

        <button className="pwa-ios-sheet__maybe" onClick={onDismiss}>Maybe Later</button>

        <div className="pwa-ios-sheet__arrow" aria-hidden="true">▼</div>
      </div>
    </div>
  );
}

function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible]               = useState(false);
  const [isIOS, setIsIOS]                   = useState(false);
  const [dismissed, setDismissed]           = useState(false);

  useEffect(() => {
    if (window.matchMedia("(display-mode: standalone)").matches) return;
    const dismissed = localStorage.getItem("pwa-prompt-dismissed");
    if (dismissed && Date.now() - Number(dismissed) < 30 * 24 * 60 * 60 * 1000) return;

    // iPadOS 13+ reports as MacIntel with touch points, not "iPad" in UA
    const ios = (/iphone|ipad|ipod/i.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) &&
      !window.MSStream;
    if (ios) {
      setIsIOS(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("pwa-prompt-dismissed", "1");
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa-prompt-dismissed", Date.now().toString());
    setDismissed(true);
    setVisible(false);
  };

  if (!visible || dismissed) return null;

  if (isIOS) return <IOSGuide onDismiss={handleDismiss} />;

  return (
    <div className="pwa-prompt" role="dialog" aria-label="Install app">
      <div className="pwa-prompt__icon">💍</div>
      <div className="pwa-prompt__text">
        <div className="pwa-prompt__title">Add Vivah Bandh to your phone</div>
        <div className="pwa-prompt__sub">Install for a faster, app-like experience</div>
      </div>
      <button className="pwa-prompt__btn" onClick={handleInstall}>Install</button>
      <button className="pwa-prompt__close" onClick={handleDismiss} aria-label="Dismiss">✕</button>
    </div>
  );
}

export default PWAInstallPrompt;
